import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function A11yPage(): Node {
  return html`
    <div>
      <h1>Accessibility (a11y)</h1>
      <p>onefold provides built-in accessibility primitives: focus trapping, live announcements, keyboard shortcuts, and skip navigation.</p>

      <h2>FocusTrap</h2>
      <p>Trap keyboard focus within a container (modals, dialogs, drawers):</p>
      ${CodeBlock(`import { html } from 'onefold';
import { FocusTrap } from 'onefold/a11y';

function Modal(content: Node): Node {
  return FocusTrap(html\`
    <div class="modal" role="dialog" aria-modal="true">
      <h2>Confirm Action</h2>
      \${content}
      <button>Cancel</button>
      <button>Confirm</button>
    </div>
  \`);
}`)}

      <h2>announce</h2>
      <p>Send messages to screen readers via a live region:</p>
      ${CodeBlock(`import { announce } from 'onefold/a11y';

// Polite announcement (waits for current speech to finish)
announce('Item added to cart');

// Assertive announcement (interrupts current speech)
announce('Form has errors. Please fix highlighted fields.', 'assertive');`)}

      <h2>useKeyboard</h2>
      <p>Declarative keyboard shortcut handling:</p>
      ${CodeBlock(`import { useKeyboard } from 'onefold/a11y';

useKeyboard({
  'Ctrl+K': () => openSearch(),
  'Escape': () => closeModal(),
  'Ctrl+S': (e) => { e.preventDefault(); save(); },
  'Alt+1': () => navigate('/'),
  'Alt+2': () => navigate('/settings'),
});`)}

      <h2>SkipLink</h2>
      <p>Skip navigation link for keyboard users:</p>
      ${CodeBlock(`import { html } from 'onefold';
import { SkipLink } from 'onefold/a11y';

function App(): Node {
  return html\`
    <div>
      \${SkipLink('#main-content', 'Skip to main content')}
      <nav><!-- navigation --></nav>
      <main id="main-content">
        <!-- main content -->
      </main>
    </div>
  \`;
}`)}

      ${Callout('SkipLink is visually hidden until focused. It becomes visible when a keyboard user tabs to it.')}

      <h2>API Reference</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>FocusTrap</code></td><td>(container: HTMLElement)</td><td>Create a focus trap. Call .activate() / .deactivate().</td></tr>
        <tr><td><code>announce</code></td><td>(message, priority?)</td><td>Announce to screen readers. Priority: 'polite' | 'assertive'.</td></tr>
        <tr><td><code>useKeyboard</code></td><td>(shortcuts: Record)</td><td>Register keyboard shortcuts. Returns { destroy() }.</td></tr>
        <tr><td><code>SkipLink</code></td><td>(target, label?)</td><td>Render a skip navigation link.</td></tr>
      </table>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { FocusTrap, announce } from 'onefold/a11y';

function App() {
  const isOpen = createSignal(false);
  let trap = null;

  function openModal() {
    isOpen.set(true);
    announce('Modal opened. Focus is trapped inside.');
    setTimeout(() => {
      const modal = document.getElementById('demo-modal');
      if (modal) {
        trap = FocusTrap(modal);
        trap.activate();
      }
    }, 0);
  }

  function closeModal() {
    if (trap) {
      trap.deactivate();
      trap = null;
    }
    isOpen.set(false);
    announce('Modal closed.');
  }

  return html\`
    <div>
      <h3>Focus Trap & announce() Demo</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">
        Uses real FocusTrap and announce from onefold/a11y.
        Open the modal, then press Tab — focus stays inside.
      </p>
      <button onclick=\${openModal}>Open Modal</button>
      \${() => isOpen() ? html\`
        <div id="demo-modal" role="dialog" aria-modal="true" aria-label="Confirm action"
          style="margin-top:16px;padding:20px;border:2px solid #3b82f6;border-radius:8px;background:#eff6ff">
          <h4 style="margin-bottom:12px">Confirm Action</h4>
          <p style="font-size:14px;margin-bottom:12px">Tab key cycles between these buttons only.</p>
          <div style="display:flex;gap:8px">
            <button onclick=\${closeModal}>Cancel</button>
            <button onclick=\${closeModal} style="background:#3b82f6;color:white;border-color:#3b82f6">Confirm</button>
          </div>
          <p style="font-size:11px;color:#666;margin-top:12px">Focus is trapped. announce() sent a screen reader message.</p>
        </div>
      \` : null}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'FocusTrap & announce — onefold/a11y')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/transitions">Transitions</a> — animate elements entering and leaving the DOM</li>
        <li><a href="/core/templates">Templates</a> — the html tagged template literal</li>
      </ul>
    </div>
  `;
}
